/**
 * OrderDetailPanel.tsx — the dispensing view for ONE order.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * Layout mirrors the production pharmacy console the dispensary already works
 * to: a persistent patient/clinical sidebar, a prescription summary with a
 * structured items table, an order-progress tracker, and a safety checklist on
 * dispense — while keeping the GPS Clinics design tokens and the compliance
 * rules below.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NO PRINT CONTROL BEFORE DISPENSING. This is a compliance control, not a UI
 * preference, and it is enforced STRUCTURALLY: while the order is awaiting
 * dispense there is no download control anywhere in this component's tree — not
 * a disabled button, not a hidden one, not one behind a `pointer-events: none`.
 * It does not exist.
 *
 * WHY it matters: if the prescription can be printed first, a document can leave
 * the building against an order the system still believes is pending, and the
 * dispense may never be confirmed. Releasing the PDF only once the dispense is
 * recorded means the paper and the record are minted by the same action. The
 * server enforces the same rule (the grant route refuses a non-dispensed order),
 * so this is defence in depth, not the control itself — but a control the person
 * can SEE and click is the one that gets used, so the UI must not offer it.
 * `pharmacy.spec.tsx` asserts the absence explicitly.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * EXACTLY TWO ACTIONS on a pending order: Cancel or Dispense & print. No "hold", no
 * "part-dispense", no free-text status. A closed action set is what makes the
 * audit trail answerable.
 *
 * ATTRIBUTIBILITY: confirming a dispense re-asks for the pharmacist code
 * (`stepUpCode`). A shift-length session on a shared dispensary workstation
 * would otherwise let someone else dispense under a colleague's session, which
 * defeats the entire reason the code exists. GPhC expects a dispensing record
 * naming a registered professional. The SERVER checks it (orders-service asks
 * identity-service, 2026-09-11 — before that only its shape was checked): a
 * wrong code is a 403 that counts towards the same lockout as sign-in, and a
 * check that could not run is a 503. The dialog words those two differently,
 * because "check your code" is the wrong advice when nobody checked it.
 *
 * A REFUSAL NO RETRY CAN FIX IS A THIRD CASE, shown in the server's own words:
 * a 409 (the clinic marked the order as a test record after this drawer opened)
 * or a 400 (it is no longer awaiting dispense — cancelled at another
 * workstation). "Please try again" there sent the pharmacist round in circles.
 * See `dispenseFailure`.
 *
 * REPLAY SAFETY: every write carries an `idempotencyKey` minted ONCE per attempt
 * and REUSED across retries, so a double-click or a "try again" after a flaky
 * response can never dispense twice or send a second patient email.
 *
 * MONEY-FREE: the detail shape has no price field. Nothing is redacted here
 * because there is nothing to redact.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NOTHING ON THE PRESCRIPTION IS INFERRED (2026-09-12).
 *
 * The prescribed-items table renders the medication name, formulation, strength
 * and directions EXACTLY as the wire supplies them, through the ONE resolver
 * every staff surface uses (`lib/prescribedLine#resolvePharmacyLine`). It
 * derives nothing, defaults nothing and parses nothing out of anything else. A
 * strength or directions the prescriber never recorded renders as a flagged
 * "Not recorded" — visible enough that a pharmacist cannot mistake absence for a
 * value — and a banner above the table says so before the rows are read.
 *
 * WHY IT IS WRITTEN THAT STRONGLY: this console previously manufactured the
 * Strength column client-side by regex over the product name, and fell back to a
 * bare em dash. See the tombstone above `itemStatusLabel` for the full account. A
 * dispensing console is the last surface before a prescription-only medicine is
 * handed to a patient; it is the wrong place for a best guess.
 *
 * ⚠️ …AND "AS THE WIRE SUPPLIES THEM" IS NOT THE SAME AS "AS A MEDICATION"
 * (2026-09-13). The resolver was reading `item.name` as a server-resolved
 * product fact on the strength of a comment. It is not one: orders `origin/dev`
 * does no catalogue lookup at all and sends the RAW STORED KEY in that field, so
 * this table printed the product key in bold under a Medication header on the
 * deployed build — the founding defect of this programme, on the screen built to
 * remove it. The console now hands the resolver the queue row's `itemLabel`
 * (`queueItemLabel`), and a Medication cell that merely repeats the token the
 * pharmacist just read in the Item column is flagged like any other absence.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * DATA MINIMISATION (UK GDPR Art 9): the clinical block is a dispensing-specific
 * extract — allergies, current medication, contraindication flags — NOT the
 * clinician's full questionnaire. A pharmacist gets what makes the dispense
 * safe; the rest of the consultation stays with the clinician.
 */
import { useCallback, useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { missingPrescriberDetail, resolvePharmacyLine } from '../lib/prescribedLine';
import {
  dispenseOrder,
  getPharmacyOrder,
  openPrescriptionPdf,
  recordTracking,
  rejectDispense,
} from '../api/pharmacy';
import type {
  OrderCaseState,
  PharmacyOrderDetail,
  PharmacyPrescriptionItem,
  PharmacyRejectionReason,
} from '../api/types';
import { formatUtcDate, formatUtcDateTime } from '../lib/format';
import { DialogShell } from '../ui/ConfirmDialog';
import { NotRecorded } from '../ui/PrescribedItems';
import { SupplyLines } from '../ui/PrescribedSupplies';
import { Alert, Badge, Button, Field, Help, Select, TextArea, TextInput } from '../ui/kit';
import { SkeletonCard } from '../ui/Skeleton';
import { DataTable, Td, Th } from '../ui/Table';
import { toneFor } from '../ui/tones';
import { pharmacyQueueLabel } from './pharmacy-labels';
import { PHARMACIST_CODE_LENGTH } from './PharmacyAuthPage';
import { TestRecordBanner } from './TestRecordNotice';
import { PHARMACY_COURIERS, trackingUrlFor } from './courier-tracking';

/** Sentinel DOB from orders-service when identity has no date — treat as "not on file". */
const UNKNOWN_DOB = '1900-01-01';

const REJECTION_REASONS: Array<{ value: PharmacyRejectionReason; label: string }> = [
  { value: 'out_of_stock', label: 'Out of stock' },
  { value: 'address_unclear', label: 'Delivery address unclear' },
  { value: 'clinical_query', label: 'Clinical query for the prescriber' },
  { value: 'cold_chain_not_assured', label: 'Cold chain cannot be assured' },
  { value: 'duplicate_order', label: 'Duplicate order' },
  { value: 'other', label: 'Other' },
];

/** The explanation attached to the disabled dispense control. */
const blockedHintStyle: CSSProperties = {
  margin: '0 0 var(--gps-s-3)',
  fontSize: '13px',
  lineHeight: 1.5,
  color: 'var(--gps-muted)',
};

const SIDEBAR_CARD: CSSProperties = {
  background: 'var(--gps-surface)',
  border: '1px solid var(--gps-border)',
  borderRadius: 'var(--gps-radius-md)',
  padding: 'var(--gps-s-3) var(--gps-s-4)',
};

const SIDEBAR_LABEL: CSSProperties = {
  margin: '0 0 var(--gps-s-3)',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--gps-muted)',
};

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function isUnknownDob(value: string): boolean {
  return value.startsWith(UNKNOWN_DOB);
}

function formatPatientDob(value: string): string {
  return isUnknownDob(value) ? 'Not on file' : formatUtcDate(value);
}

function formatPatientName(value: string): string {
  return value === 'Not recorded' ? 'Not on file' : value;
}

function patientAge(dob: string): number | null {
  if (isUnknownDob(dob)) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDelta = today.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
}

/**
 * ═════════════════════════════════════════════════════════════════════════════
 * WHERE `parseMedicationStrength` USED TO BE — AND WHY IT IS GONE FOR GOOD.
 *
 * This module used to derive the Strength column client-side, by regex-scraping
 * a trailing quantity off `item.name`:
 *
 *     /^(.+?)\s+([\d.]+\s*(?:mg|mcg|g|ml|units?)(?:\/[\w-]+)?)$/i
 *
 * It did that because the wire carried no strength at all, and it fell back to a
 * bare em dash when the scrape failed. Three things were wrong with it, and all
 * three are patient-safety issues rather than tidiness:
 *
 *  1. IT INVENTED A CLINICAL VALUE. Whatever that regex captured was printed in
 *     a column headed "Strength" in a DISPENSING console, with nothing marking
 *     it as inferred from a display label rather than prescribed by a human.
 *  2. IT WAS AIMED STRAIGHT AT A PRICING ANCHOR. The pattern matches the
 *     orders-side catalogue format exactly. That figure is the prices-service
 *     plan anchor — the dearest variant each family is priced against, a
 *     commercial number — and for the device families it sits several-fold above
 *     the dose treatment starts at. One catalogue rename would have put it on
 *     this screen with no code change at all.
 *  3. ITS ABSENT CASE WAS A SILENT DASH. "—" reads as "nothing to say here", not
 *     "nobody recorded this", and is visually identical to a legitimately empty
 *     optional field.
 *
 * The strength now comes from `item.strength` — the server's snapshot of the
 * variant the prescriber CHOSE on the prescribe form — or from nowhere. Do not
 * reintroduce a parser, a fallback, or a default. If a future reader wants a
 * strength on this screen and the wire does not have one, the fix is upstream,
 * not a guess here.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/* ⚠️ THIS CONSOLE'S TWO PRIVATE ABSENCE MARKERS ARE GONE (2026-09-13).
 *
 * `NotRecordedFlag` (amber badge, for strength/directions) and
 * `NotRecordedQuiet` (grey text, for formulation) said the same two words in
 * two styles, beside a THIRD wording on the clinician's own record of the same
 * prescription. A pharmacist and a prescriber looking at one line must be
 * reading one sentence about one gap, so every staff surface now renders
 * `ui/PrescribedItems#NotRecorded`.
 *
 * The clinical-vs-catalogue distinction those two markers carried is NOT lost:
 * it lives in the banner above the table, which fires on
 * `lib/prescribedLine#missingPrescriberDetail` — the two PRESCRIBING facts
 * only — and tells the pharmacist to query them with the prescriber. That is a
 * better place for it than a colour difference between two cells.
 */

function itemStatusLabel(state: OrderCaseState): string | null {
  if (state === 'awaiting_dispense' || state === 'dispense_rejected') return null;
  return 'Dispensed';
}

export function OrderDetailPanel({
  orderCaseId,
  queueItemLabel,
  onChanged,
  onOrderRefLoaded,
  includeTest = false,
}: {
  orderCaseId: string;
  /**
   * THE `itemLabel` OFF THE QUEUE ROW THIS DRAWER WAS OPENED FROM — the token
   * the pharmacist has just read in the Item column, and the one thing on this
   * screen that can tell a dispensing name from a machine default.
   *
   * The detail payload carries no `productKey` field on either deployed orders
   * build, so without this the Medication cell has nothing to check `item.name`
   * against — and `dev` puts the RAW STORED KEY in `item.name` on every
   * auto-issued row. That is how a product key came to print as the medication
   * on the screen stock is picked from. See `lib/prescribedLine#resolvePharmacyLine`.
   */
  queueItemLabel: string | null;
  onChanged: () => void;
  /** Fires when the detail read returns so the drawer title can show the real ref. */
  onOrderRefLoaded?: (orderRef: string) => void;
  /**
   * Mirrors the queue's "Show test orders" filter. A flagged order is 404 on the
   * detail read unless this is on, so the drawer has to carry through whatever
   * the list behind it was showing.
   */
  includeTest?: boolean;
}) {
  const [detail, setDetail] = useState<PharmacyOrderDetail | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'unavailable'>('loading');
  const [focusPrescription, setFocusPrescription] = useState(false);
  const prescriptionSectionId = `pharmacy-prescription-${orderCaseId}`;
  const testBlockedHintId = `pharmacy-test-blocked-${orderCaseId}`;

  const load = useCallback(() => {
    setStatus('loading');
    getPharmacyOrder(orderCaseId, includeTest)
      .then((loaded) => {
        setDetail(loaded);
        setStatus('ready');
        onOrderRefLoaded?.(loaded.orderRef);
      })
      .catch((error: unknown) => {
        setStatus(error instanceof ApiError && error.status === 404 ? 'unavailable' : 'error');
      });
  }, [orderCaseId, includeTest, onOrderRefLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  const afterWrite = useCallback(
    (options?: { focusPrescription?: boolean }) => {
      if (options?.focusPrescription) setFocusPrescription(true);
      load();
      onChanged();
    },
    [load, onChanged],
  );

  useEffect(() => {
    if (!focusPrescription || !detail || detail.state === 'awaiting_dispense') return;
    document.getElementById(prescriptionSectionId)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    setFocusPrescription(false);
  }, [detail, focusPrescription, prescriptionSectionId]);

  if (status === 'loading') return <SkeletonCard lines={6} label="Loading the order…" />;
  if (status === 'unavailable') {
    return (
      <Alert tone="info" role="status">
        The full dispensing view for this order isn&rsquo;t available yet. Please don&rsquo;t dispense from
        the queue list alone — the prescription, the delivery address and the clinical check all belong on
        this screen.
      </Alert>
    );
  }
  if (status === 'error' || !detail) {
    return (
      <Alert tone="danger" role="alert">
        Sorry — we could not load this order.{' '}
        <button
          type="button"
          onClick={load}
          style={{
            border: 0,
            background: 'none',
            padding: 0,
            font: 'inherit',
            fontWeight: 700,
            color: 'inherit',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </Alert>
    );
  }

  // ⚠️ THE ORDER OF THESE TWO LINES IS THE RULE. A test order is never
  // actionable, whatever state it is in, so the flag gates the actions rather
  // than sitting beside them: `awaitingDispense` and `dispensed` are what every
  // action block below keys on, and folding the check in HERE means a block
  // added later inherits it instead of having to remember it.
  //
  // This is defence in depth, not the control. orders-service refuses dispense,
  // reject, tracking and the prescription release for a flagged order server-side
  // — see PHARMACY_TEST_RECORD_REFUSAL. Hiding a button only stops the honest
  // mistake; the server stops the rest.
  const isTestRecord = Boolean(detail.testRecord);
  const awaitingDispense = detail.state === 'awaiting_dispense' && !isTestRecord;
  const dispensed = (detail.state === 'dispensed' || detail.state === 'dispatched') && !isTestRecord;
  const age = patientAge(detail.patient.dateOfBirth);

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      {/* FIRST in the drawer, above the state banner. It is the most important
          fact about this panel and it decides how everything under it should be
          read. */}
      {detail.testRecord ? <TestRecordBanner testRecord={detail.testRecord} /> : null}
      <OrderStatusBanner detail={detail} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(240px, 300px) minmax(0, 1fr)',
          gap: 'var(--gps-s-4)',
          alignItems: 'start',
        }}
      >
        <aside style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
          <SidebarCard title="Patient details">
            <SidebarRow label="Full name" value={formatPatientName(detail.patient.fullName)} />
            <SidebarRow label="Date of birth" value={formatPatientDob(detail.patient.dateOfBirth)} />
            {age != null ? <SidebarRow label="Age" value={String(age)} /> : null}
            <SidebarRow label="Phone" value={detail.patient.phone ?? 'Not on file'} />
            <div style={{ marginTop: 'var(--gps-s-2)' }}>
              <p style={{ ...SIDEBAR_LABEL, marginBottom: '6px' }}>Delivery address</p>
              <DeliveryAddressBlock lines={detail.patient.deliveryAddress} />
            </div>
          </SidebarCard>

          <SidebarCard title="Clinical check">
            {detail.clinical.contraindications.length > 0 ? (
              <Alert tone="warning" style={{ marginBottom: 'var(--gps-s-2)' }}>
                <strong>Flags:</strong> {detail.clinical.contraindications.join(', ')}
              </Alert>
            ) : null}
            <SidebarRow label="Allergies" value={detail.clinical.allergies ?? 'None recorded'} highlight={Boolean(detail.clinical.allergies)} />
            <SidebarRow label="Current medication" value={detail.clinical.currentMedication ?? 'None recorded'} />
          </SidebarCard>

          <SidebarCard title="Prescribing clinician">
            <SidebarRow label="Name" value={formatPatientName(detail.prescriber.fullName)} />
            <SidebarRow label="Registration" value={detail.prescriber.registrationNumber ?? 'Not on file'} />
            <SidebarRow label="Clinic" value={detail.clinicName} />
          </SidebarCard>
        </aside>

        <div style={{ display: 'grid', gap: 'var(--gps-s-4)', minWidth: 0 }}>
          <section
            style={{
              background: 'var(--gps-surface)',
              border: '1px solid var(--gps-border)',
              borderRadius: 'var(--gps-radius-md)',
              padding: 'var(--gps-s-4)',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 'var(--gps-s-3)',
                marginBottom: 'var(--gps-s-3)',
              }}
            >
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: 700, color: 'var(--gps-text)' }}>
                  Prescription summary
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--gps-muted)' }}>
                  Order {detail.orderRef} · prescribed {formatUtcDate(detail.issuedAt)}
                </p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                <Badge tone={toneFor(detail.state === 'dispatched' ? 'dispensed' : detail.state)}>
                  {pharmacyQueueLabel(detail.state)}
                </Badge>
                {awaitingDispense ? (
                  <PendingActions
                    orderCaseId={detail.orderCaseId}
                    onDone={(options) => afterWrite(options)}
                    inline
                  />
                ) : null}
                {/* ⚠️ A DISABLED BUTTON, NOT AN ABSENT ONE — and this is the one
                    place in this console where that is the right call. The
                    no-print-before-dispensing rule works by ABSENCE (see the file
                    header) because there the pharmacist has no reason to expect a
                    control. Here they do: they came looking for this order
                    because an email named it, on a row that still says
                    "Awaiting dispense". A missing button reads as a bug and gets
                    reported; a disabled one with a reason attached answers the
                    question they actually have. The server refuses it either
                    way. */}
                {isTestRecord && detail.state === 'awaiting_dispense' ? (
                  <Button
                    type="button"
                    variant="cta"
                    style={{ width: 'auto' }}
                    disabled
                    aria-describedby={testBlockedHintId}
                  >
                    Dispense &amp; print
                  </Button>
                ) : null}
              </div>
            </div>

            {/* `disabled` alone announces "unavailable" and says nothing about
                why. The hint is a real element referenced by aria-describedby, so
                a screen reader reads the reason as part of the button rather than
                leaving it as sighted-only text somewhere on the page. Visible
                too: a sighted pharmacist has exactly the same question. */}
            {isTestRecord && detail.state === 'awaiting_dispense' ? (
              <p id={testBlockedHintId} style={blockedHintStyle}>
                Dispensing is switched off for test orders. If you think this is a real patient order,
                contact the clinic before doing anything with it.
              </p>
            ) : null}

            <SuppliesToInclude supplies={detail.supplies} />

            <PrescribedItemsTable
              items={detail.items}
              orderState={detail.state}
              queueItemLabel={queueItemLabel}
            />
          </section>

          <ClinicalNotesPanel clinical={detail.clinical} />

          {dispensed ? (
            <DispensedActions detail={detail} onDone={afterWrite} prescriptionSectionId={prescriptionSectionId} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ── Order status banner (legacy console: one status, not a multi-step tracker) ─ */

function OrderStatusBanner({ detail }: { detail: PharmacyOrderDetail }) {
  if (detail.state === 'dispense_rejected') {
    return (
      <Alert tone="danger" role="status" style={{ marginBottom: 0 }}>
        This order has been cancelled. The GP Service admin team has been notified and will take it from here — the
        patient has not been told.
      </Alert>
    );
  }

  if (detail.state === 'dispensed' || detail.state === 'dispatched') {
    return (
      <Alert tone="info" role="status" style={{ marginBottom: 0, background: 'var(--gps-primary-light)', borderColor: 'var(--gps-primary-tint-strong)' }}>
        Dispensed on {detail.dispensedAt ? formatUtcDateTime(detail.dispensedAt) : 'record'}{' '}
        {detail.dispensedByName ? `by ${detail.dispensedByName}` : 'against your account'}.
        {detail.state === 'dispatched' && detail.dispatchedAt
          ? ` Tracking saved on ${formatUtcDateTime(detail.dispatchedAt)}.`
          : ''}
      </Alert>
    );
  }

  return null;
}

/* ── Sidebar primitives ─────────────────────────────────────────────────────── */

function SidebarCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={SIDEBAR_CARD}>
      <h3 style={SIDEBAR_LABEL}>{title}</h3>
      {children}
    </section>
  );
}

function SidebarRow({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <dl style={{ margin: '0 0 10px' }}>
      <dt style={{ margin: 0, fontSize: '12px', color: 'var(--gps-muted)' }}>{label}</dt>
      <dd
        style={{
          margin: '2px 0 0',
          fontSize: '14px',
          fontWeight: highlight ? 700 : 500,
          color: highlight ? 'var(--gps-danger-text)' : 'var(--gps-text)',
        }}
      >
        {value}
      </dd>
    </dl>
  );
}

function DeliveryAddressBlock({ lines }: { lines: string[] }) {
  if (lines.length === 0) {
    return <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--gps-muted)' }}>No delivery address on file</p>;
  }
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--gps-radius-sm)',
        background: 'var(--gps-primary-light)',
        border: '1px solid var(--gps-primary-tint-strong)',
      }}
    >
      {lines.map((line) => (
        <span key={line} style={{ display: 'block', fontSize: '13.5px', color: 'var(--gps-text)', lineHeight: 1.45 }}>
          {line}
        </span>
      ))}
    </div>
  );
}

/* ── Supplies to include (2026-09-24) ──────────────────────────────────────── */

/**
 * THE SUPPLIES TO DISPENSE WITH THIS ORDER — directly above the prescribed items,
 * where a pharmacist cannot pick the order without reading it.
 *
 * WHY THIS BLOCK EXISTS: a live patient received her pens with nothing to use
 * them with. The clinician had asked for them in the prescription NOTES, and
 * notes reach neither the dispensing email nor this table. Supplies are now a
 * structured part of the prescription, and on this console they get their own
 * alert rather than a row in the medication table — a supply has no strength,
 * formulation or directions, so a table row would render three "Not recorded"
 * flags under something that is complete, and fire the missing-detail banner.
 *
 * The heading says what the list IS in words (never colour alone, WCAG 1.4.1),
 * and each line is the server's name and quantity through the one shared
 * formatter, so it reads exactly as the PDF and the email do. `role="status"`,
 * not `alert`: it is information present on load, not an interruption.
 *
 * Nothing renders when the list is empty, or when an older orders build sends
 * no `supplies` key at all — this console shows what it was before.
 */
function SuppliesToInclude({ supplies }: { supplies: PharmacyOrderDetail['supplies'] }) {
  if (!supplies || supplies.length === 0) return null;
  return (
    <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
      <strong style={{ display: 'block', fontWeight: 700 }}>Supplies to include</strong>
      <span style={{ display: 'block', margin: '2px 0 6px' }}>
        Dispense these with the prescribed items below. They are part of the prescription.
      </span>
      <SupplyLines supplies={supplies} label="Supplies to include" />
    </Alert>
  );
}

/* ── Prescribed items table ─────────────────────────────────────────────────── */

function PrescribedItemsTable({
  items,
  orderState,
  queueItemLabel,
}: {
  items: PharmacyPrescriptionItem[];
  orderState: OrderCaseState;
  queueItemLabel: string | null;
}) {
  const statusLabel = itemStatusLabel(orderState);
  const showItemStatus = statusLabel != null;
  // EVERY VALUE IN THIS TABLE COMES OUT OF THE ONE RESOLVER (2026-09-13). The
  // console chooses the LAYOUT — a table, because a pharmacist compares lines
  // down a column — and chooses none of the values. The clinician's record of
  // the same prescription is a definition list over the identical resolver, so
  // the two cannot state a different medication, strength, formulation,
  // directions or quantity for one line.
  const lines = items.map((item) => ({ item, line: resolvePharmacyLine(item, queueItemLabel) }));
  // One banner for the whole table rather than one per cell: a pharmacist
  // scanning a multi-line prescription needs to know BEFORE they read the rows
  // that something on it was never written down.
  const anyUnrecorded = lines.some(({ line }) => missingPrescriberDetail(line));

  return (
    <>
      {anyUnrecorded ? (
        <Alert tone="warning" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
          <strong style={{ display: 'block', fontWeight: 600 }}>
            This prescription does not record a strength or directions for every item.
          </strong>
          <span style={{ display: 'block', marginTop: '4px' }}>
            The missing values are shown as <em>Not recorded</em> below. Nothing has been inferred or filled in for
            you. Query them with the prescriber before dispensing.
          </span>
        </Alert>
      ) : null}
      <DataTable
        label="Prescribed items"
        head={
          <>
            <Th>Medication</Th>
            <Th>Formulation</Th>
            <Th>Strength</Th>
            {/* "Directions", not "Dosage": this column is the prescriber's own
                instruction to the patient, and the contract field is
                `directions`. Naming it "Dosage" next to a Strength column
                invited the two to be read as one split value, which is exactly
                how a prescriber's own words got cut in half upstream. */}
            <Th>Directions</Th>
            {/* "Quantity", not "Qty" — the same word the clinician's own
                prescription record uses, so the two staff-facing surfaces label
                the field identically as well as filling it identically. */}
            <Th>Quantity</Th>
            {showItemStatus ? <Th>Status</Th> : null}
          </>
        }
      >
        {lines.map(({ item, line }, index) => (
          // The row key is positional on purpose: a prescription may legitimately
          // carry the same product twice at different strengths, the list is never
          // reordered or filtered, and keying off resolved clinical values would
          // collide precisely on the unrecorded rows this table exists to show.
          <tr key={`${line.name ?? 'unnamed'}-${index}`}>
            <Td>
              <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                {/* THE RESOLVED NAME OR THE FLAG — never a product key, never a
                    fragment split off something else. This cell used to print
                    `item.name` unconditionally, so a name the server could not
                    resolve would have rendered blank in the Medication column of
                    a DISPENSING console. */}
                {line.name === null ? (
                  <NotRecorded label="Medication" />
                ) : (
                  <span style={{ fontWeight: 600 }}>{line.name}</span>
                )}
                {item.coldChain ? <Badge tone="pending">Cold chain</Badge> : null}
              </span>
            </Td>
            <Td>{line.formulation ?? <NotRecorded label="Formulation" />}</Td>
            <Td>{line.strength ?? <NotRecorded label="Strength" />}</Td>
            <Td>{line.directions ?? <NotRecorded label="Directions" />}</Td>
            {/* THE SERVER'S OWN WORDING, or the bare recorded number. This
                cell used to print `{item.quantity}` raw: a bare "3" under a
                "Qty" header, beside a pre-filled-device description, on the
                screen a pharmacist picks stock from — while the PDF that
                pharmacist signs for and the dispensing email both said
                "3 packs" for the same line. Nothing is formatted here; see
                `lib/prescribedLine` for why the browser must not invent the
                unit. */}
            <Td>{line.quantityText ?? <NotRecorded label="Quantity" />}</Td>
            {showItemStatus ? (
              <Td>
                <span style={{ color: 'var(--gps-green-text)', fontWeight: 600 }}>✓ {statusLabel}</span>
              </Td>
            ) : null}
          </tr>
        ))}
      </DataTable>
    </>
  );
}

/* ── Expandable clinical notes ────────────────────────────────────────────────── */

function ClinicalNotesPanel({ clinical }: { clinical: PharmacyOrderDetail['clinical'] }) {
  const [open, setOpen] = useState(Boolean(clinical.clinicianNotes));
  const hasContent =
    Boolean(clinical.clinicianNotes) ||
    clinical.contraindications.length > 0 ||
    Boolean(clinical.allergies) ||
    Boolean(clinical.currentMedication);

  if (!hasContent) return null;

  return (
    <section
      style={{
        background: 'var(--gps-surface)',
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '14px var(--gps-s-4)',
          border: 0,
          background: 'var(--gps-surface-2)',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--gps-text)',
          textAlign: 'left',
        }}
      >
        Clinician notes &amp; clinical context
        <span aria-hidden="true" style={{ color: 'var(--gps-muted)', fontSize: '18px' }}>
          {open ? '−' : '+'}
        </span>
      </button>
      {open ? (
        <div style={{ padding: 'var(--gps-s-3) var(--gps-s-4)' }}>
          {clinical.clinicianNotes ? (
            <p style={{ margin: '0 0 var(--gps-s-2)', fontSize: '14px', color: 'var(--gps-text)', lineHeight: 1.55 }}>
              {clinical.clinicianNotes}
            </p>
          ) : (
            <Help style={{ marginBottom: 0 }}>No additional notes from the prescriber.</Help>
          )}
        </div>
      ) : null}
    </section>
  );
}

/* ── Pending: the ONLY two actions ─────────────────────────────────────────── */

function PendingActions({
  orderCaseId,
  onDone,
  inline = false,
}: {
  orderCaseId: string;
  onDone: (options?: { focusPrescription?: boolean }) => void;
  inline?: boolean;
}) {
  const [dialog, setDialog] = useState<'none' | 'dispense' | 'cancel'>('none');

  const buttons = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      <Button type="button" variant="ghost" style={{ width: 'auto' }} onClick={() => setDialog('cancel')}>
        Cancel order
      </Button>
      <Button type="button" variant="cta" style={{ width: 'auto' }} onClick={() => setDialog('dispense')}>
        Dispense &amp; print
      </Button>
    </div>
  );

  return (
    <>
      {inline ? (
        buttons
      ) : (
        <section>
          <Help style={{ marginBottom: 'var(--gps-s-2)' }}>
            Confirm the dispense to release the watermarked prescription for printing. The PDF is not available
            before you dispense.
          </Help>
          {buttons}
        </section>
      )}

      {dialog === 'dispense' ? (
        <DispenseDialog
          orderCaseId={orderCaseId}
          onCancel={() => setDialog('none')}
          onDone={() => {
            setDialog('none');
            onDone({ focusPrescription: true });
          }}
        />
      ) : null}

      {dialog === 'cancel' ? (
        <CancelDialog
          orderCaseId={orderCaseId}
          onCancel={() => setDialog('none')}
          onDone={() => {
            setDialog('none');
            onDone();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * What the dispense dialog says when the write fails. Decided on the STATUS
 * first, and only then — for the two statuses that carry one — on the server's
 * sentence.
 *
 *  - 403 → `code`, the FIXED refused-code copy. `dispenseOrder` parses bodies,
 *    so the 403's own sentence is sitting in `detail`; it is ignored on purpose.
 *    A wrong code, a locked pharmacist and a withdrawn one must read identically,
 *    or the dialog becomes an oracle for which it was.
 *  - 409 / 400 WITH a sentence → `refused`, rendered VERBATIM. orders-service
 *    answers 409 for an order the clinic marked as a test record and 400 for one
 *    that is no longer awaiting dispense. No retry can succeed, so the house
 *    "please try again" would send the pharmacist round in circles. Both are
 *    Nest `{message}` strings orders-service writes itself, carrying no patient
 *    data (R19).
 *  - everything else → `other`, the house line: a 503 (the code check could not
 *    run — its body talks about the code, which is exactly the wrong advice),
 *    any other 5xx, a network fault, and a 400/409 with NO sentence. orders'
 *    schema 400 is `{formErrors, fieldErrors}` with no `message`, and an empty
 *    alert or a dump of field names is worse than the house line.
 */
type DispenseFailure = { kind: 'code' } | { kind: 'refused'; reason: string } | { kind: 'other' };

function dispenseFailure(error: unknown): DispenseFailure {
  if (!(error instanceof ApiError)) return { kind: 'other' };
  if (error.status === 403) return { kind: 'code' };
  const reason = error.detail?.trim();
  if ((error.status === 409 || error.status === 400) && reason) return { kind: 'refused', reason };
  return { kind: 'other' };
}

function DispenseDialog({
  orderCaseId,
  onCancel,
  onDone,
}: {
  orderCaseId: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  // WHICH failure, not just whether. Since 2026-09-11 the server really checks
  // the code, so a 403 means it was refused — and a 503 means the check could
  // not run, which "check your code" would misdescribe (the code may be right).
  // A refusal no retry can fix carries the server's own sentence — see
  // `dispenseFailure`.
  const [failure, setFailure] = useState<DispenseFailure | null>(null);
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [checks, setChecks] = useState({ reviewed: false, stock: false, clinical: false });

  const allChecked = checks.reviewed && checks.stock && checks.clinical;
  const ready = allChecked && code.trim().length === PHARMACIST_CODE_LENGTH;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      await dispenseOrder(orderCaseId, { confirmed: true, stepUpCode: code.trim(), idempotencyKey });
      onDone();
    } catch (error) {
      setFailure(dispenseFailure(error));
      setBusy(false);
    }
  }

  return (
    <DialogShell title="Confirm this dispense" onCancel={busy ? () => {} : onCancel}>
      <form onSubmit={onSubmit} aria-label="Confirm this dispense">
        <fieldset style={{ border: 0, margin: '0 0 var(--gps-s-3)', padding: 0 }}>
          <legend style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gps-text)', marginBottom: '10px' }}>
            Before you dispense
          </legend>
          <ChecklistItem
            id="check-reviewed"
            checked={checks.reviewed}
            disabled={busy}
            onChange={(checked) => setChecks((prev) => ({ ...prev, reviewed: checked }))}
            label="I have reviewed all items and the delivery address."
          />
          <ChecklistItem
            id="check-stock"
            checked={checks.stock}
            disabled={busy}
            onChange={(checked) => setChecks((prev) => ({ ...prev, stock: checked }))}
            label="All items are in stock, correctly labelled, and ready to dispense."
          />
          <ChecklistItem
            id="check-clinical"
            checked={checks.clinical}
            disabled={busy}
            onChange={(checked) => setChecks((prev) => ({ ...prev, clinical: checked }))}
            label="I have reviewed the allergies, flags and clinician notes."
          />
        </fieldset>

        <Alert tone="info" style={{ marginBottom: 'var(--gps-s-3)' }}>
          Once confirmed, use <strong>Open prescription</strong> below to view and print the watermarked PDF in a
          new tab. You can open it again at any time — the DISPENSED watermark will always appear.
        </Alert>

        <Field
          label="Your pharmacist code"
          htmlFor="dispense-step-up"
          hint={`${PHARMACIST_CODE_LENGTH} characters — the same code you signed in with.`}
        >
          <TextInput
            id="dispense-step-up"
            type="password"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={PHARMACIST_CODE_LENGTH}
            autoComplete="one-time-code"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={busy || !allChecked}
          />
        </Field>
        {failure?.kind === 'code' ? (
          <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            That pharmacist code was not accepted, so this order has not been dispensed. Check the code and try
            again — several wrong codes in a row pause dispensing for a short while.
          </Alert>
        ) : null}
        {failure?.kind === 'refused' ? (
          // `warning`, not `danger`: nothing went wrong and nobody erred — the
          // order moved on. The drawer's own test-order banner wears the same tone.
          <Alert tone="warning" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {failure.reason}
          </Alert>
        ) : null}
        {failure?.kind === 'other' ? (
          <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            We could not record that dispense just now — this order has not been dispensed. Please try again.
          </Alert>
        ) : null}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="submit" variant="cta" disabled={!ready || busy} style={{ width: 'auto' }}>
            {busy ? 'Recording…' : 'Confirm dispense & print'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} style={{ width: 'auto' }}>
            Cancel
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

function ChecklistItem({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      htmlFor={id}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        marginBottom: '10px',
        fontSize: '14px',
        color: 'var(--gps-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0 }}
      />
      <span>{label}</span>
    </label>
  );
}

function CancelDialog({
  orderCaseId,
  onCancel,
  onDone,
}: {
  orderCaseId: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState<PharmacyRejectionReason>('out_of_stock');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [idempotencyKey] = useState(newIdempotencyKey);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await rejectDispense(orderCaseId, {
        reason,
        detail: detail.trim() || undefined,
        idempotencyKey,
      });
      onDone();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }

  return (
    <DialogShell title="Cancel this order" onCancel={busy ? () => {} : onCancel}>
      <form onSubmit={onSubmit} aria-label="Cancel this order">
        <Alert tone="info" style={{ marginBottom: 'var(--gps-s-3)' }}>
          The order will be marked as cancelled. The GP Service admin team is notified so they can put it right.
          The patient is not told.
        </Alert>
        <Field label="Reason" htmlFor="cancel-reason">
          <Select
            id="cancel-reason"
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value as PharmacyRejectionReason)}
          >
            {REJECTION_REASONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Anything the admin team should know"
          htmlFor="cancel-detail"
          hint="Optional. Goes to the admin team and the audit record only."
        >
          <TextArea id="cancel-detail" value={detail} disabled={busy} onChange={(e) => setDetail(e.target.value)} />
        </Field>
        {failed ? (
          <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            We could not cancel that order. Please try again — this order is unchanged.
          </Alert>
        ) : null}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="submit" variant="cta" disabled={busy} style={{ width: 'auto', background: 'var(--gps-danger-strong)' }}>
            {busy ? 'Cancelling…' : 'Cancel order'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} style={{ width: 'auto' }}>
            Back
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

/* ── Dispensed: the prescription is released, then courier + tracking ──────── */

function DispensedActions({
  detail,
  onDone,
  prescriptionSectionId,
}: {
  detail: PharmacyOrderDetail;
  onDone: () => void;
  prescriptionSectionId: string;
}) {
  return (
    <>
      <section
        id={prescriptionSectionId}
        style={{
          background: 'var(--gps-surface)',
          border: '1px solid var(--gps-border)',
          borderRadius: 'var(--gps-radius-md)',
          padding: 'var(--gps-s-4)',
        }}
      >
        <h3 style={{ ...SIDEBAR_LABEL, marginBottom: 'var(--gps-s-2)' }}>Step 1 — Print the prescription</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--gps-s-3)', marginBottom: 'var(--gps-s-3)' }}>
          <MetricTile label="Dispensed" value={detail.dispensedAt ? formatUtcDateTime(detail.dispensedAt) : 'Recorded'} />
          <MetricTile label="Dispensed by" value={detail.dispensedByName ?? 'Recorded against your account'} />
        </div>
        <OpenPrescriptionButton orderCaseId={detail.orderCaseId} />
      </section>

      <section
        style={{
          background: 'var(--gps-surface)',
          border: '1px solid var(--gps-border)',
          borderRadius: 'var(--gps-radius-md)',
          padding: 'var(--gps-s-4)',
        }}
      >
        <h3 style={{ ...SIDEBAR_LABEL, marginBottom: 'var(--gps-s-3)' }}>
          Step 2 — {detail.state === 'dispatched' ? 'Delivery tracking' : 'Add courier & tracking'}
        </h3>
        {detail.state === 'dispatched' ? (
          <TrackingSummary
            courier={detail.courier}
            trackingNumber={detail.trackingNumber}
          />
        ) : (
          <>
            <Alert tone="info" style={{ marginBottom: 'var(--gps-s-3)' }}>
              Open the prescription first, then choose the courier and enter the tracking number from the label.
            </Alert>
            <TrackingForm orderCaseId={detail.orderCaseId} onDone={onDone} />
          </>
        )}
      </section>
    </>
  );
}

function MetricTile({
  label,
  value,
  mono = false,
  href,
  hrefLabel,
}: {
  label: string;
  value: string;
  mono?: boolean;
  /** When set, the value itself is a carrier link (tracking number → courier site). */
  href?: string | null;
  hrefLabel?: string;
}) {
  const fontFamily = mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit';

  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: 'var(--gps-radius-sm)',
        background: 'var(--gps-surface-2)',
        border: '1px solid var(--gps-border)',
      }}
    >
      <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--gps-muted)' }}>
        {label}
      </p>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={hrefLabel}
          style={{
            margin: '4px 0 0',
            display: 'inline-block',
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--gps-link)',
            fontFamily,
            wordBreak: 'break-all',
            textDecoration: 'underline',
          }}
        >
          {value}
        </a>
      ) : (
        <p
          style={{
            margin: '4px 0 0',
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--gps-text)',
            fontFamily,
            wordBreak: 'break-all',
          }}
        >
          {value}
        </p>
      )}
    </div>
  );
}

function OpenPrescriptionButton({ orderCaseId }: { orderCaseId: string }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function onOpen() {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await openPrescriptionPdf(orderCaseId);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Button type="button" variant="primary" style={{ width: 'auto' }} disabled={busy} onClick={onOpen}>
        {busy ? 'Opening…' : 'Open prescription'}
      </Button>
      <Help style={{ margin: '8px 0 0' }}>Watermarked DISPENSED — opens in a new tab for printing.</Help>
      {failed ? (
        <Alert tone="danger" role="alert" style={{ marginTop: 'var(--gps-s-2)' }}>
          We could not open the prescription. Confirm the order is dispensed and try again.
        </Alert>
      ) : null}
    </div>
  );
}

function TrackingSummary({
  courier,
  trackingNumber,
}: {
  courier?: string | null;
  trackingNumber?: string | null;
}) {
  const trackingUrl =
    courier && trackingNumber ? trackingUrlFor(courier, trackingNumber) : null;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--gps-s-3)' }}>
        <MetricTile label="Delivery method" value={courier ?? '—'} />
        <MetricTile
          label="Tracking number"
          value={trackingNumber ?? '—'}
          mono
          href={trackingUrl}
          hrefLabel={
            trackingUrl
              ? `Open ${courier ?? 'carrier'} tracking for ${trackingNumber ?? 'parcel'}`
              : undefined
          }
        />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: 'var(--gps-s-3)', alignItems: 'center' }}>
        {trackingUrl ? (
          <a
            href={trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '10px 20px',
              borderRadius: 'var(--gps-radius-sm)',
              background: 'var(--gps-primary)',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 700,
              textDecoration: 'none',
              border: '1px solid var(--gps-primary-dark)',
            }}
          >
            Open tracking
          </a>
        ) : null}
        <Help style={{ margin: 0 }}>
          {trackingUrl
            ? 'The patient has been sent these tracking details. Click the tracking number (or Open tracking) to follow the parcel on the carrier site.'
            : 'Tracking saved. The patient has been notified — open the carrier site manually if needed.'}
        </Help>
      </div>
    </div>
  );
}

function TrackingForm({ orderCaseId, onDone }: { orderCaseId: string; onDone: () => void }) {
  const [courier, setCourier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [idempotencyKey] = useState(newIdempotencyKey);

  const ready = courier.length > 0 && trackingNumber.trim().length > 0;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await recordTracking(orderCaseId, {
        courier,
        trackingNumber: trackingNumber.trim(),
        idempotencyKey,
      });
      onDone();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} aria-label="Courier and tracking">
      <Help>Select the delivery method and enter the tracking number from the carrier label.</Help>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--gps-s-3)' }}>
        <Field label="Select delivery method" htmlFor="tracking-courier">
          <Select
            id="tracking-courier"
            value={courier}
            disabled={busy}
            onChange={(e) => setCourier(e.target.value)}
          >
            <option value="">Choose a courier…</option>
            {PHARMACY_COURIERS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Tracking number" htmlFor="tracking-number" hint="From the carrier label or dispatch note.">
          <TextInput
            id="tracking-number"
            value={trackingNumber}
            disabled={busy}
            maxLength={64}
            autoComplete="off"
            placeholder="Enter tracking number"
            onChange={(e) => setTrackingNumber(e.target.value)}
          />
        </Field>
      </div>
      {failed ? (
        <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
          We could not save those tracking details. Please try again.
        </Alert>
      ) : null}
      <Button type="submit" variant="cta" style={{ width: 'auto' }} disabled={!ready || busy}>
        {busy ? 'Saving…' : 'Save tracking'}
      </Button>
    </form>
  );
}
